import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ExternalLink, 
  Calendar, 
  Users, 
  TrendingUp, 
  Smartphone, 
  CheckCircle,
  Sparkles
} from 'lucide-react';

export default function FeaturedCaseStudy() {
  const [activeTab, setActiveTab] = useState('booking');

  const tabs = [
    { id: 'booking', label: 'Appointment Booking', icon: Calendar },
    { id: 'staff', label: 'Staff & Payroll', icon: Users },
    { id: 'analytics', label: 'Revenue Analytics', icon: TrendingUp },
    { id: 'crm', label: 'Customer CRM', icon: Smartphone }
  ];

  const features = [
    'Real-time live appointment booking slots',
    'Interactive staff timetables and check-ins',
    'Automatic payroll calculations based on shifts',
    'Daily, weekly, and monthly revenue analytics',
    'Comprehensive customer history profiles & notes',
    'Mobile-first responsive interface design'
  ];

  return (
    <section id="case-study" className="py-24 md:py-32 relative bg-[#EFE7DD]/40 border-y border-[#8B6B4A]/10" data-testid="case-study-section">
      {/* Background Lights */}
      <div className="absolute top-1/4 left-1/3 glowing-blob blob-gold opacity-[0.06]" />
      <div className="absolute bottom-1/4 right-1/3 glowing-blob blob-beige opacity-[0.05]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        
        {/* Section Title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#8B6B4A]/5 border border-[#8B6B4A]/10 rounded-full px-4 py-1.5 backdrop-blur-md mb-4">
              <Sparkles className="w-3.5 h-3.5 text-[#C8A97E]" />
              <span className="text-xs font-semibold tracking-wider uppercase text-[#8B6B4A] font-poppins">
                Highlighted Case Study
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight">
              11 Studio Hair Studio
            </h2>
            <p className="text-[#6B625B] font-poppins text-base sm:text-lg mt-2 max-w-xl">
              Building a full-fledged enterprise management ecosystem for a premium hair salon brand.
            </p>
          </div>
          <a
            href="https://11studio.saloncredence.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white/70 hover:bg-[#8B6B4A]/10 border border-[#8B6B4A]/15 hover:border-[#8B6B4A]/30 text-[#3E2F24] hover:text-[#8B6B4A] rounded-full px-6 py-3 font-medium transition-all shadow-sm active:scale-95 font-poppins text-sm w-fit"
          >
            Visit Live Project
            <ExternalLink className="w-4 h-4 text-[#8B6B4A]" />
          </a>
        </div>

        {/* Case Study Grid */}
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          
          {/* Column 1: Info */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#8B6B4A] font-space block">The Challenge</span>
              <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed">
                Hair salons rely heavily on fluid schedules. 11 Studio faced friction with manual pen-and-paper appointment books, leading to double-bookings, staff scheduling conflicts, and poor overview of commission payrolls.
              </p>
            </div>

            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#8B6B4A] font-space block">The Solution</span>
              <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed">
                We engineered a high-performance web platform containing a clean client booking front-end and a robust administrative panel. The custom platform automates service slots, handles commission matrices, monitors attendance, and calculates real-time analytics.
              </p>
            </div>

            {/* Feature Bullet Points */}
            <div className="space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#8B6B4A] font-space block">System Deliverables</span>
              <div className="grid sm:grid-cols-2 gap-3">
                {features.map((feat, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#8B6B4A] mt-0.5 flex-shrink-0" />
                    <span className="text-xs font-poppins text-[#6B625B] leading-normal">{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Column 2: Dashboard Mockup */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl overflow-hidden bg-white/70 border border-[#8B6B4A]/15 shadow-md backdrop-blur-md" data-testid="dashboard-mockup">
              
              {/* Window Header */}
              <div className="mockup-window-header">
                <div className="mockup-dot bg-[#8B6B4A]/30" />
                <div className="mockup-dot bg-[#C8A97E]/30" />
                <div className="mockup-dot bg-[#D6C2A8]/30" />
                <span className="text-[10px] text-[#6B625B] font-space ml-4 select-none">11studio.saloncredence.com/admin/dashboard</span>
              </div>

              {/* Tabs list inside Mockup */}
              <div className="flex bg-[#8B6B4A]/5 border-b border-[#8B6B4A]/10 overflow-x-auto select-none no-scrollbar">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-5 py-3 text-xs font-medium font-poppins transition-all border-b-2 whitespace-nowrap ${
                      activeTab === tab.id 
                        ? 'border-[#8B6B4A] text-[#3E2F24] bg-white/50' 
                        : 'border-transparent text-[#6B625B] hover:text-[#3E2F24]'
                    }`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Mockup Display Box */}
              <div className="p-6 min-h-[340px] bg-white/30">
                <AnimatePresence mode="wait">
                  
                  {/* Appointment Booking View */}
                  {activeTab === 'booking' && (
                    <motion.div
                      key="booking"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-4"
                    >
                      <div className="flex justify-between items-center bg-white/80 p-3 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#8B6B4A] animate-pulse" />
                          <div>
                            <div className="text-xs font-semibold text-[#3E2F24] font-poppins">Balayage Masterclass</div>
                            <div className="text-[10px] text-[#6B625B]">Stylist: Elena Rostova</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-0.5 text-[9px] font-bold bg-[#8B6B4A]/10 text-[#8B6B4A] rounded-full">Confirmed</span>
                          <div className="text-[10px] text-[#6B625B] mt-1">Today, 2:00 PM</div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center bg-white/80 p-3 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#C8A97E] animate-pulse" />
                          <div>
                            <div className="text-xs font-semibold text-[#3E2F24] font-poppins">Gentleman Trim & Shave</div>
                            <div className="text-[10px] text-[#6B625B]">Stylist: Marcus Vance</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-0.5 text-[9px] font-bold bg-[#C8A97E]/10 text-[#8B6B4A] rounded-full">Active</span>
                          <div className="text-[10px] text-[#6B625B] mt-1">Today, 3:30 PM</div>
                        </div>
                      </div>

                      <div className="flex justify-between items-center bg-white/80 p-3 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full bg-[#D6C2A8]" />
                          <div>
                            <div className="text-xs font-semibold text-[#3E2F24] font-poppins">Keratin Therapy</div>
                            <div className="text-[10px] text-[#6B625B]">Stylist: Elena Rostova</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="px-2 py-0.5 text-[9px] font-bold bg-[#D6C2A8]/30 text-[#6B625B] rounded-full">Pending</span>
                          <div className="text-[10px] text-[#6B625B] mt-1">Tomorrow, 10:00 AM</div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Staff & Payroll View */}
                  {activeTab === 'staff' && (
                    <motion.div
                      key="staff"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/80 p-4 rounded-2xl border border-[#8B6B4A]/10 shadow-sm text-center">
                          <div className="w-10 h-10 rounded-full bg-[#8B6B4A]/10 text-[#8B6B4A] mx-auto flex items-center justify-center font-bold text-xs">ER</div>
                          <div className="text-xs font-bold text-[#3E2F24] font-space mt-2">Elena Rostova</div>
                          <div className="text-[10px] text-[#6B625B]">Senior Colorist</div>
                          <div className="mt-3 flex justify-between text-[10px] border-t border-[#8B6B4A]/10 pt-2">
                            <span className="text-[#6B625B]">Monthly Commission</span>
                            <span className="text-[#8B6B4A] font-bold">$1,450.00</span>
                          </div>
                        </div>

                        <div className="bg-white/80 p-4 rounded-2xl border border-[#8B6B4A]/10 shadow-sm text-center">
                          <div className="w-10 h-10 rounded-full bg-[#C8A97E]/10 text-[#8B6B4A] mx-auto flex items-center justify-center font-bold text-xs">MV</div>
                          <div className="text-xs font-bold text-[#3E2F24] font-space mt-2">Marcus Vance</div>
                          <div className="text-[10px] text-[#6B625B]">Master Barber</div>
                          <div className="mt-3 flex justify-between text-[10px] border-t border-[#8B6B4A]/10 pt-2">
                            <span className="text-[#6B625B]">Monthly Commission</span>
                            <span className="text-[#8B6B4A] font-bold">$1,120.00</span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Revenue Analytics View */}
                  {activeTab === 'analytics' && (
                    <motion.div
                      key="analytics"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white/80 p-3 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                          <div className="text-[10px] text-[#6B625B] font-poppins">Daily Gross</div>
                          <div className="text-base font-bold text-[#3E2F24] font-space mt-1">$945.00</div>
                          <span className="text-[9px] text-[#8B6B4A] font-medium">+12.4%</span>
                        </div>
                        <div className="bg-white/80 p-3 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                          <div className="text-[10px] text-[#6B625B] font-poppins">Active Clients</div>
                          <div className="text-base font-bold text-[#3E2F24] font-space mt-1">284</div>
                          <span className="text-[9px] text-[#8B6B4A] font-medium">+8.1%</span>
                        </div>
                        <div className="bg-white/80 p-3 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                          <div className="text-[10px] text-[#6B625B] font-poppins">Avg. Ticket Value</div>
                          <div className="text-base font-bold text-[#3E2F24] font-space mt-1">$72.50</div>
                          <span className="text-[9px] text-[#6B625B] font-medium">-1.2%</span>
                        </div>
                      </div>

                      {/* Micro Bar Chart */}
                      <div className="bg-white/80 p-4 rounded-xl border border-[#8B6B4A]/10 shadow-sm">
                        <div className="text-[10px] text-[#6B625B] mb-2">Weekly Performance Comparison</div>
                        <div className="flex items-end justify-between h-20 pt-4 px-2">
                          <div className="w-6 bg-[#8B6B4A]/60 rounded-t-sm h-12" title="Mon" />
                          <div className="w-6 bg-[#C8A97E]/60 rounded-t-sm h-16" title="Tue" />
                          <div className="w-6 bg-[#8B6B4A]/60 rounded-t-sm h-14" title="Wed" />
                          <div className="w-6 bg-[#D6C2A8]/80 rounded-t-sm h-20" title="Thu" />
                          <div className="w-6 bg-[#C8A97E]/60 rounded-t-sm h-10" title="Fri" />
                          <div className="w-6 bg-[#8B6B4A]/60 rounded-t-sm h-18" title="Sat" />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Customer CRM View */}
                  {activeTab === 'crm' && (
                    <motion.div
                      key="crm"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="space-y-3"
                    >
                      <table className="w-full text-left text-xs font-poppins text-[#6B625B]">
                        <thead>
                          <tr className="border-b border-[#8B6B4A]/10 text-[#3E2F24]">
                            <th className="py-2">Client Name</th>
                            <th className="py-2">Last Visit</th>
                            <th className="py-2">Status</th>
                            <th className="py-2 text-right">Loyalty Pts</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-[#8B6B4A]/5">
                            <td className="py-2 text-[#3E2F24] font-medium">Sophia Loren</td>
                            <td className="py-2">May 24, 2026</td>
                            <td className="py-2"><span className="px-1.5 py-0.5 text-[8px] bg-[#8B6B4A]/10 text-[#8B6B4A] rounded-full font-bold">VIP Gold</span></td>
                            <td className="py-2 text-right text-[#8B6B4A] font-bold">850</td>
                          </tr>
                          <tr className="border-b border-[#8B6B4A]/5">
                            <td className="py-2 text-[#3E2F24] font-medium">David Beckham</td>
                            <td className="py-2">May 21, 2026</td>
                            <td className="py-2"><span className="px-1.5 py-0.5 text-[8px] bg-[#C8A97E]/10 text-[#8B6B4A] rounded-full font-bold">Regular</span></td>
                            <td className="py-2 text-right text-[#8B6B4A] font-bold">420</td>
                          </tr>
                          <tr className="border-b border-[#8B6B4A]/5">
                            <td className="py-2 text-[#3E2F24] font-medium">Scarlett Johansson</td>
                            <td className="py-2">May 15, 2026</td>
                            <td className="py-2"><span className="px-1.5 py-0.5 text-[8px] bg-[#8B6B4A]/10 text-[#8B6B4A] rounded-full font-bold">VIP Gold</span></td>
                            <td className="py-2 text-right text-[#8B6B4A] font-bold">980</td>
                          </tr>
                        </tbody>
                      </table>
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
